import { useState, useEffect } from 'react'

const API = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}`

function AdminDashboard() {
    const [dashboard, setDashboard] = useState(null)
    const [categorias, setCategorias] = useState([])
    const [cidades, setCidades] = useState([])
    const [loading, setLoading] = useState(true)
    const [erro, setErro] = useState('')
    const [dataInicio, setDataInicio] = useState('')
    const [dataFim, setDataFim] = useState('')
    const [periodoPreset, setPeriodoPreset] = useState('tudo')
    const [lastUpdated, setLastUpdated] = useState(null)
    const [statusDot, setStatusDot] = useState('loading')

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
    }

    useEffect(() => {
        carregarDados()
    }, [])

    const carregarDados = async (startOverride = null, endOverride = null) => {
        if (!dashboard) {
            setLoading(true)
        }
        setStatusDot('loading')
        setErro('')
        try {
            const params = new URLSearchParams()
            const sDate = startOverride !== null ? startOverride : dataInicio
            const eDate = endOverride !== null ? endOverride : dataFim
            
            if (sDate) params.append('dataInicio', sDate)
            if (eDate) params.append('dataFim', eDate)

            const [dashRes, catRes, cidRes] = await Promise.all([
                fetch(`${API}/api/admin/dashboard?${params}`, { headers }),
                fetch(`${API}/api/admin/analytics/categorias?${params}`, { headers }),
                fetch(`${API}/api/admin/analytics/cidades?${params}`, { headers })
            ])

            if (!dashRes.ok) throw new Error('Erro ao carregar dashboard')

            const dashData = await dashRes.json()
            setDashboard(dashData)

            if (catRes.ok) {
                const catData = await catRes.json()
                setCategorias(catData.categorias || catData || [])
            }

            if (cidRes.ok) {
                const cidData = await cidRes.json()
                setCidades(cidData.cidades || cidData || [])
            }

            setStatusDot('online')
            setLastUpdated(new Date().toLocaleTimeString('pt-BR'))
            setErro('')
        } catch (error) {
            console.error('Erro ao carregar dashboard:', error)
            setErro('Não foi possível carregar os dados atualizados do servidor.')
            setStatusDot('offline')
        } finally {
            setLoading(false)
        }
    }

    const handleFiltrar = () => {
        carregarDados(dataInicio, dataFim)
    }

    const aplicarPreset = (preset) => {
        setPeriodoPreset(preset)
        
        const formatISODate = (d) => {
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        }

        const hoje = new Date()
        let inicio = ''
        let fim = ''

        if (preset === 'hoje') {
            inicio = formatISODate(hoje)
            fim = formatISODate(hoje)
        } else if (preset === 'ontem') {
            const ontem = new Date()
            ontem.setDate(hoje.getDate() - 1)
            inicio = formatISODate(ontem)
            fim = formatISODate(ontem)
        } else if (preset === '7d') {
            const seteDias = new Date()
            seteDias.setDate(hoje.getDate() - 6)
            inicio = formatISODate(seteDias)
            fim = formatISODate(hoje)
        } else if (preset === '14d') {
            const quatorzeDias = new Date()
            quatorzeDias.setDate(hoje.getDate() - 13)
            inicio = formatISODate(quatorzeDias)
            fim = formatISODate(hoje)
        } else if (preset === '30d') {
            const trintaDias = new Date()
            trintaDias.setDate(hoje.getDate() - 29)
            inicio = formatISODate(trintaDias)
            fim = formatISODate(hoje)
        }

        setDataInicio(inicio)
        setDataFim(fim)
        
        carregarDados(inicio, fim)
    }

    const formatDate = (date) => new Date(date).toLocaleDateString('pt-BR')

    if (loading && !dashboard) {
        return (
            <div className="admin-loading">
                <div className="spinner"></div>
                <span>Carregando dashboard...</span>
            </div>
        )
    }

    if (erro && !dashboard) {
        return (
            <div className="admin-error" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                color: '#fff',
                textAlign: 'center',
                gap: '1rem',
                padding: '2rem'
            }}>
                <div style={{ fontSize: '3rem' }}>⚠️</div>
                <h3 style={{ margin: 0, color: '#ef4444' }}>Falha na Conexão com o Servidor</h3>
                <p style={{ color: '#94a3b8', maxWidth: '400px', margin: '0 auto' }}>
                    O painel administrador não conseguiu se conectar à API. Verifique sua conexão ou se o servidor está online.
                </p>
                <button 
                    onClick={() => carregarDados()}
                    style={{
                        background: '#10b981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.6rem 1.5rem',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        transition: 'background 0.2s'
                    }}
                >
                    🔄 Tentar Novamente
                </button>
            </div>
        )
    }

    const kpis = [
        { icon: '👥', value: dashboard?.totalUsuarios || 0, label: 'Total Usuários' },
        { icon: '📢', value: dashboard?.totalAnunciosAtivos || 0, label: 'Anúncios Ativos' },
        { icon: '🤝', value: dashboard?.totalVendas || 0, label: 'Vendas Realizadas' },
        { icon: '⏰', value: dashboard?.totalExpirados || 0, label: 'Anúncios Expirados' },
        { icon: '🆕', value: dashboard?.novosUsuarios7d || 0, label: 'Novos Usuários' },
    ]

    const maxCat = categorias.length > 0 ? Math.max(...categorias.map(c => c.total || c.count || 0)) : 1

    return (
        <div>
            <style>{`
                @keyframes pulse {
                    0% { opacity: 0.4; }
                    50% { opacity: 1; }
                    100% { opacity: 0.4; }
                }
            `}</style>

            {/* Barra de Status e Sincronização em Tempo Real */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#1e293b',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                padding: '0.75rem 1.25rem',
                marginBottom: '1.25rem',
                flexWrap: 'wrap',
                gap: '0.75rem'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            display: 'inline-block',
                            background: statusDot === 'online' ? '#10b981' : statusDot === 'offline' ? '#ef4444' : '#f59e0b',
                            boxShadow: statusDot === 'online' 
                                ? '0 0 8px #10b981' 
                                : statusDot === 'offline' 
                                ? '0 0 8px #ef4444' 
                                : '0 0 8px #f59e0b',
                            animation: statusDot === 'loading' ? 'pulse 1.5s infinite' : 'none'
                        }} />
                        <span style={{
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            color: statusDot === 'online' ? '#10b981' : statusDot === 'offline' ? '#ef4444' : '#f59e0b'
                        }}>
                            {statusDot === 'online' ? 'API Online' : statusDot === 'offline' ? 'API Offline' : 'Atualizando dados...'}
                        </span>
                    </div>

                    {lastUpdated && (
                        <span style={{ fontSize: '0.8rem', color: '#64748b', borderLeft: '1px solid rgba(255,255,255,0.08)', paddingLeft: '0.75rem' }}>
                            Última sincronização: <strong>{lastUpdated}</strong>
                        </span>
                    )}
                </div>

                <button
                    onClick={() => carregarDados()}
                    disabled={statusDot === 'loading'}
                    style={{
                        background: 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        borderRadius: '6px',
                        color: '#94a3b8',
                        padding: '0.35rem 0.75rem',
                        fontSize: '0.8rem',
                        cursor: statusDot === 'loading' ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s',
                        outline: 'none'
                    }}
                    onMouseOver={(e) => {
                        if (statusDot !== 'loading') {
                            e.currentTarget.style.color = '#fff';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                        }
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.color = '#94a3b8';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <span>🔄</span> {statusDot === 'loading' ? 'Sincronizando...' : 'Sincronizar Agora'}
                </button>
            </div>

            {/* Banner de erro quando offline mas com dados anteriores */}
            {erro && (
                <div style={{
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.15)',
                    borderRadius: '8px',
                    padding: '0.85rem 1.25rem',
                    marginBottom: '1.25rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#ef4444',
                    fontSize: '0.85rem'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>⚠️</span>
                        <span>{erro} Exibindo dados em cache.</span>
                    </div>
                </div>
            )}

            {/* Filtros de Data e Presets no Dashboard */}
            <div className="admin-section" style={{ padding: '1.25rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' }}>📊 Filtro de Período</h3>
                <div className="admin-date-range" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                    <select 
                        value={periodoPreset} 
                        onChange={(e) => aplicarPreset(e.target.value)}
                        style={{ 
                            background: '#1e293b', 
                            border: '1px solid rgba(255,255,255,0.1)', 
                            borderRadius: '6px', 
                            color: '#fff', 
                            padding: '0.45rem 1rem', 
                            fontSize: '0.85rem',
                            outline: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="tudo">Todos os Períodos 📅</option>
                        <option value="hoje">Hoje ⏳</option>
                        <option value="ontem">Ontem ⏰</option>
                        <option value="7d">Últimos 7 dias 📆</option>
                        <option value="14d">Últimos 14 dias 📅</option>
                        <option value="30d">Últimos 30 dias 🗓️</option>
                        <option value="personalizado">Personalizado 🛠️</option>
                    </select>

                    <label style={{ fontSize: '0.85rem', color: '#94a3b8' }}>De:</label>
                    <input
                        type="date"
                        value={dataInicio}
                        onChange={(e) => {
                            setPeriodoPreset('personalizado');
                            setDataInicio(e.target.value);
                        }}
                        style={{
                            background: '#1e293b',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '6px',
                            color: '#fff',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.85rem',
                            outline: 'none'
                        }}
                    />
                    <label style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Até:</label>
                    <input
                        type="date"
                        value={dataFim}
                        onChange={(e) => {
                            setPeriodoPreset('personalizado');
                            setDataFim(e.target.value);
                        }}
                        style={{
                            background: '#1e293b',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '6px',
                            color: '#fff',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.85rem',
                            outline: 'none'
                        }}
                    />
                    <button className="admin-action-btn success" onClick={handleFiltrar} style={{ padding: '0.45rem 1.25rem', fontSize: '0.85rem' }}>
                        Filtrar
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="admin-kpi-grid">
                {kpis.map((kpi, i) => (
                    <div key={i} className="admin-kpi-card">
                        <div className="admin-kpi-icon">{kpi.icon}</div>
                        <div className="admin-kpi-value">{kpi.value.toLocaleString('pt-BR')}</div>
                        <div className="admin-kpi-label">{kpi.label}</div>
                    </div>
                ))}
            </div>

            <div className="admin-grid-2">
                {/* Ranking de Categorias */}
                <div className="admin-section">
                    <div className="admin-section-header">
                        <h3>📊 Ranking de Categorias</h3>
                    </div>
                    {categorias.length > 0 ? (
                        <div className="admin-bar-chart">
                            {categorias.slice(0, 8).map((cat, i) => {
                                const val = cat.total || cat.count || 0
                                const pct = maxCat > 0 ? (val / maxCat) * 100 : 0
                                return (
                                    <div key={i} className="admin-bar-item">
                                        <span className="admin-bar-label">{cat.categoria || cat.nome || cat.name}</span>
                                        <div className="admin-bar-track">
                                            <div className="admin-bar-fill" style={{ width: `${Math.max(pct, 5)}%` }}>
                                                {val}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="admin-empty">
                            <p>Nenhum dado de categorias disponível.</p>
                        </div>
                    )}
                </div>

                {/* Top 5 Cidades */}
                <div className="admin-section">
                    <div className="admin-section-header">
                        <h3>🏙️ Top 5 Cidades</h3>
                    </div>
                    {cidades.length > 0 ? (
                        <div className="admin-bar-chart">
                            {cidades.slice(0, 5).map((cid, i) => {
                                const val = cid.total || cid.count || 0
                                const maxCid = cidades[0]?.total || cidades[0]?.count || 1
                                const pct = (val / maxCid) * 100
                                return (
                                    <div key={i} className="admin-bar-item">
                                        <span className="admin-bar-label">{cid.cidade || cid.nome || cid.name}</span>
                                        <div className="admin-bar-track">
                                            <div className="admin-bar-fill" style={{ width: `${Math.max(pct, 5)}%` }}>
                                                {val}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="admin-empty">
                            <p>Nenhum dado de cidades disponível.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Últimos Usuários */}
            <div className="admin-section">
                <div className="admin-section-header">
                    <h3>🆕 Últimos Usuários Cadastrados</h3>
                </div>
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Email</th>
                                <th>Tipo</th>
                                <th>Cadastro</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(dashboard?.ultimosUsuarios || []).length > 0 ? (
                                dashboard.ultimosUsuarios.map(u => {
                                    const tipos = [u.tipo || 'comprador']
                                    const isPro = (u.assinatura_ilimitada || u.plano_pro || u.planoPro) && u.expiracao_assinatura && new Date(u.expiracao_assinatura) > new Date();
                                    if (isPro) tipos.push('pro')
                                    if (u.tipo_ativo === 'motorista' && !tipos.includes('motorista')) tipos.push('motorista')
                                    if (u.vendas_realizadas > 0 && !tipos.includes('vendedor')) tipos.push('vendedor')
                                    if (u.cargo === 'admin' && !tipos.includes('admin')) tipos.push('admin')
                                    
                                    const uniqueTipos = Array.from(new Set(tipos.map(t => t.toUpperCase())))

                                    return (
                                        <tr key={u.id}>
                                            <td data-label="Nome" style={{ color: '#f8fafc', fontWeight: 500 }}>{u.nome}</td>
                                            <td data-label="Email">{u.email}</td>
                                            <td data-label="Tipo">
                                                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                    {uniqueTipos.map((t, idx) => (
                                                        <span key={idx} className={`admin-badge ${t === 'PRO' ? 'admin-badge-pro' : 'admin-badge-ativo'}`}>
                                                            {t}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td data-label="Cadastro">{formatDate(u.criado_em || u.criadoEm)}</td>
                                        </tr>
                                    )
                                })
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                        Nenhum usuário recente.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Últimos Logs */}
            <div className="admin-section">
                <div className="admin-section-header">
                    <h3>📋 Últimos Logs</h3>
                </div>
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Admin</th>
                                <th>Ação</th>
                                <th>Alvo</th>
                                <th>Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(dashboard?.ultimosLogs || []).length > 0 ? (
                                dashboard.ultimosLogs.map((log, i) => (
                                    <tr key={log.id || i}>
                                        <td data-label="Admin" style={{ color: '#f8fafc', fontWeight: 500 }}>{log.admin_nome || log.adminNome || '—'}</td>
                                        <td data-label="Ação">{log.acao}</td>
                                        <td data-label="Alvo">{log.alvo || '—'}</td>
                                        <td data-label="Data">{formatDate(log.criado_em || log.criadoEm)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                                        Nenhum log recente.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

export default AdminDashboard
